"use client"

import { useState, useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { Bar, Line, Pie } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js"
import Sidebar from "./Sidebar"
import Header from "./Header"
import LoadingScreen from "./LoadingScreen"
import ReactDatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { format } from "date-fns"
import UsageReports from "./UsageReports"

const ACCOUNT_LEVEL_PROJECT = "__account_level__"

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement)

// Skeleton Components for Billing Reports
const BillingSummarySkeleton = () => (
  <div className="animate-pulse">
    <div className="flex items-center gap-2 mb-2">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
    </div>
    <div className="mt-4">
      <div className="h-8 bg-gray-200 rounded w-2/3 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-5/6"></div>
    </div>
  </div>
)

const TopServicesSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex items-center gap-2 mb-2">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
    </div>
    <div className="mt-4 space-y-3">
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-200 rounded-full mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3 mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-200 rounded-full mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4 mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    </div>
  </div>
)

const MainChartSkeleton = () => {
  // Static heights for consistent appearance
  const barHeights = [
    85, 92, 76, 88, 95, 73, 81, 97, 90, 78,
    82, 69, 74, 99, 87, 91, 77, 84, 93, 80,
    71, 96, 79, 100, 89, 72, 83, 98, 86, 94
  ];

  return (
    <div className="animate-pulse">
      <div className="mb-4 sm:mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-1"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="h-[400px] flex items-end justify-between px-4 py-8 space-x-1">
        {barHeights.map((height, i) => (
          <div key={i} className="flex flex-col items-center space-y-2 flex-1 max-w-[24px]">
            <div className="w-full flex flex-col space-y-0.5">
              <div className="bg-gray-200 rounded-t w-full" style={{ height: `${height}px` }}></div>
              <div className="bg-gray-300 w-full" style={{ height: `${Math.floor(height * 0.6)}px` }}></div>
              <div className="bg-gray-200 rounded-b w-full" style={{ height: `${Math.floor(height * 0.4)}px` }}></div>
            </div>
            {i % 5 === 0 && <div className="h-2 bg-gray-200 rounded w-6"></div>}
          </div>
        ))}
      </div>
    </div>
  )
}

const TopResourcesSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="space-y-3 divide-y divide-gray-100">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex justify-between py-2">
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
        </div>
      ))}
    </div>
  </div>
)

const PieChartSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
    <div className="h-[300px] flex items-center justify-center">
      <div className="relative">
        <div className="w-48 h-48 border-8 border-gray-200 rounded-full"></div>
      </div>
    </div>
  </div>
)

const UsageReportsSkeleton = () => (
  <div className="animate-pulse bg-white rounded-lg shadow-sm p-4 sm:p-6">
    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
    <div className="space-y-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
        </div>
      ))}
    </div>
  </div>
)

// Helper functions
const formatCurrency = (value) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`
}

// Color palette for services
const serviceColors = {
  "Cloud SQL": "#4F7CFF",
  Networking: "#FF7C4F",
  "Compute Engine": "#36A2EB",
  "VM Manager": "#9966FF",
  "Cloud Storage": "#FF9F40",
  "Cloud Logging": "#4BC0C0",
  "Cloud Monitoring": "#F87979",
  "Source Repository": "#7FC8A9",
  "Geocoding API": "#FF6B6B",
  "Maps JavaScript API": "#4ECDC4",
}

// Get a color for a service, with fallback
const getServiceColor = (serviceName, index) => {
  return serviceColors[serviceName] || `hsl(${(index * 360) / Object.keys(serviceColors).length}, 70%, 60%)`
}

// Get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date()
  return format(now, "yyyy-MM")
}

const getMonthEnd = (month) => {
  if (!month || typeof month !== "string") return ""
  const [year, monthIndex] = month.split("-").map(Number)
  return format(new Date(year, monthIndex, 0), "yyyy-MM-dd")
}

const getPreviousMonth = (month) => {
  if (!month || typeof month !== "string") return ""
  const [year, monthIndex] = month.split("-").map(Number)
  return format(new Date(year, monthIndex - 2, 1), "yyyy-MM")
}

// Check if selected month is current month
const isCurrentMonth = (selectedMonth) => {
  const currentMonth = getCurrentMonth()
  return selectedMonth === currentMonth
}

const toLocalDate = (value) => new Date(`${value}T00:00:00`)

const savingsOptions = [
  { key: "negotiated_savings", label: "Savings programmes (negotiated pricing)" },
  { key: "spend_based_cud_savings", label: "Spend-based CUD discounts" },
  { key: "legacy_cud_savings", label: "Legacy spend-based CUD credits" },
  { key: "free_tier_savings", label: "Free tier credits" },
  { key: "promotional_savings", label: "Promotional credits" },
  { key: "sud_savings", label: "Sustained use discounts (SUDs)" },
  { key: "spending_based_savings", label: "Spending-based discounts" },
]

export default function BillingReports() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedProjectIds = (searchParams.get("project_ids") || searchParams.get("project_id") || "")
    .split(",")
    .filter(Boolean)
  const requestedBillingId = searchParams.get("billing_account_id")
  const requestedFrom = searchParams.get("from")
  const requestedTo = searchParams.get("to")
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true))
  const [showCumulative, setShowCumulative] = useState(false)
  const [chartType, setChartType] = useState("bar")
  const [isCompactChart, setIsCompactChart] = useState(() => window.innerWidth < 1024)
  const [processedData, setProcessedData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isChartLoading, setIsChartLoading] = useState(true)
  const [selectedMonth, setSelectedMonth] = useState(() => requestedFrom?.slice(0, 7) || getCurrentMonth())
  const [selectedProjects, setSelectedProjects] = useState(requestedProjectIds)
  const [selectedBillingId, setSelectedBillingId] = useState(null)
  const [billingData, setBillingData] = useState(null)
  const [historicalBillingData, setHistoricalBillingData] = useState([])
  const [billingAccounts, setBillingAccounts] = useState([])
  const [availableProjects, setAvailableProjects] = useState([])
  const [projectMenuOpen, setProjectMenuOpen] = useState(false)
  const [pendingProjects, setPendingProjects] = useState(requestedProjectIds)
  const [projectSearch, setProjectSearch] = useState("")
  const [billingSummary, setBillingSummary] = useState({
    totalCost: 0,
    prevTotalCost: null,
    overallChangePercent: null,
    topService: null,
    topServiceCost: 0,
    topServiceChangePercent: null,
    secondService: null,
    secondServiceCost: 0,
    secondServiceChangePercent: null,
    serviceCount: 0,
    monthName: format(new Date(selectedMonth), "MMMM yyyy"),
  })
  const [invoiceData, setInvoiceData] = useState(null)
  const [savingsMenuOpen, setSavingsMenuOpen] = useState(false)
  const [enabledSavings, setEnabledSavings] = useState(() =>
    Object.fromEntries(savingsOptions.map((option) => [option.key, true])),
  )
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}")
  const isAdmin = currentUser.role === "Admin"

  const reportFrom = requestedFrom || `${selectedMonth}-01`
  const reportTo = requestedTo || getMonthEnd(selectedMonth)
  const selectedAccount = billingAccounts.find((account) => account.billing_account_id === selectedBillingId)

  useEffect(() => {
    const updateChartDensity = () => setIsCompactChart(window.innerWidth < 1024)
    window.addEventListener("resize", updateChartDensity)
    return () => window.removeEventListener("resize", updateChartDensity)
  }, [])

  const updateReportUrl = (projects, billingAccountId, month) => {
    const params = {
      billing_account_id: billingAccountId,
      from: `${month}-01`,
      to: getMonthEnd(month),
    }
    if (projects?.length) params.project_ids = projects.join(",")
    setSearchParams(params)
  }

  const savingsTotals = useMemo(() => {
    const totals = Object.fromEntries(savingsOptions.map((option) => [option.key, 0]))
    const groupedData = billingData?.["billing by service and project data"] || {}
    Object.values(groupedData).flat().forEach((row) => {
      savingsOptions.forEach((option) => {
        totals[option.key] += Number(row[option.key] || 0)
      })
    })
    return totals
  }, [billingData])

  const appliedSavings = savingsOptions.reduce(
    (total, option) => total + (enabledSavings[option.key] ? Math.abs(savingsTotals[option.key]) : 0),
    0,
  )

  const getDailyTotals = (data) => {
      const grouped = data?.["billing by service and project data"] || {}
      return Object.entries(grouped).map(([date, rows]) => ({
        date,
        total: rows.reduce((sum, row) => sum + Number(row.subtotal || 0) + savingsOptions.reduce(
          (saving, option) => saving + (enabledSavings[option.key] ? Number(row[option.key] || 0) : 0),
          0,
        ), 0),
      })).sort((a, b) => a.date.localeCompare(b.date))
  }

  const billingInsights = useMemo(() => {
    const currentDays = getDailyTotals(billingData)
    const previousDays = getDailyTotals(historicalBillingData[0])
    const [year, monthIndex] = selectedMonth.split("-").map(Number)
    const daysInMonth = new Date(year, monthIndex, 0).getDate()
    const actualDays = currentDays.filter((day) => day.date.startsWith(selectedMonth) && Math.abs(day.total) > 0)
    const latestDay = actualDays.length ? toLocalDate(actualDays[actualDays.length - 1].date).getDate() : 0
    const currentTotal = actualDays.reduce((sum, day) => sum + day.total, 0)
    const isOngoingMonth = selectedMonth === getCurrentMonth() && latestDay > 0 && latestDay < daysInMonth
    const previousComparable = previousDays
      .filter((day) => !isOngoingMonth || new Date(day.date).getDate() <= latestDay)
      .reduce((sum, day) => sum + day.total, 0)
    const previousFullMonth = previousDays.reduce((sum, day) => sum + day.total, 0)
    const changePercent = previousComparable ? ((currentTotal - previousComparable) / Math.abs(previousComparable)) * 100 : null
    const completedDays = actualDays.slice(0, latestDay > 0 ? -1 : undefined)
    const baseDays = completedDays.length ? completedDays : actualDays
    const flatDailyAverage = baseDays.length
      ? baseDays.reduce((sum, day) => sum + day.total, 0) / baseDays.length
      : 0
    const lastActualDate = actualDays[actualDays.length - 1]?.date

    const forecastDays = isOngoingMonth ? Array.from({ length: daysInMonth - latestDay + 1 }, (_, index) => {
      const date = format(new Date(year, monthIndex - 1, latestDay + index), "yyyy-MM-dd")
      const actualTotal = date === lastActualDate ? actualDays[actualDays.length - 1].total : 0
      const projectedTotal = flatDailyAverage

      // For ongoing month, keep remaining estimate flat/sejajar across all future days
      return { date, total: Math.max(projectedTotal - actualTotal, 0), projectedTotal }
    }) : []
    const forecast = currentTotal + forecastDays.reduce((sum, day) => sum + day.total, 0)
    const forecastPercent = previousFullMonth ? ((forecast - previousFullMonth) / Math.abs(previousFullMonth)) * 100 : null

    return { currentTotal, latestDay, daysInMonth, isOngoingMonth, changePercent, forecast, forecastPercent, forecastDays }
  }, [billingData, historicalBillingData, enabledSavings, selectedMonth])

  const toggleSidebar = () => {
    setSidebarOpen((prevState) => !prevState)
  }

  const projectOptions = useMemo(() => [
    { value: ACCOUNT_LEVEL_PROJECT, label: "[Charges not specific to a project]", detail: "No project" },
    ...availableProjects.filter((project) => project.billing_id === selectedBillingId),
  ], [availableProjects, selectedBillingId])

  const filteredProjectOptions = projectOptions.filter((project) => {
    const search = projectSearch.trim().toLowerCase()
    return !search || `${project.label} ${project.detail || project.value}`.toLowerCase().includes(search)
  })

  const openProjectMenu = () => {
    setPendingProjects(selectedProjects)
    setProjectSearch("")
    setProjectMenuOpen(true)
  }

  const togglePendingProject = (projectId) => {
    setPendingProjects((current) => current.includes(projectId)
      ? current.filter((value) => value !== projectId)
      : [...current, projectId])
  }

  const applyProjectFilter = () => {
    setSelectedProjects(pendingProjects)
    updateReportUrl(pendingProjects, selectedBillingId, selectedMonth)
    setProjectMenuOpen(false)
  }

  const fetchProjectsForBillingAccount = async (billingAccountId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/billing/projects?billing_account_id=${encodeURIComponent(billingAccountId)}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
    )
    if (!response.ok) throw new Error(`Project API request failed with status ${response.status}`)
    const data = await response.json()
    setAvailableProjects(data.map((item) => ({ value: item.project_id, label: item.project_id, billing_id: billingAccountId })))
  }

  // Fetch billing data from API
  useEffect(() => {
    const fetchBillingData = async () => {
      setIsChartLoading(true)
      try {
        // Ambil billingAccountId dari selectedBillingId, bukan localStorage
        const billingAccountId = selectedBillingId || "01E318-E504D7-6A5BBF"

        const params = new URLSearchParams({
          billing_account_id: billingAccountId,
          year_month: `${selectedMonth}-01`,
          from: reportFrom,
          to: reportTo,
        })
        if (selectedProjects.length) params.set("project_ids", selectedProjects.join(","))
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-by-service-by-project?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`)
        }

        const data = await response.json()

        // Check if we have valid data
        if (
          data &&
          data["billing by service and project data"] &&
          Object.keys(data["billing by service and project data"]).length > 0
        ) {
          setBillingData(data)
          setIsChartLoading(false) // Only set to false when we have valid data
          console.log("Fetched billing data:", data)
        } else {
          console.log("No billing data available")
          setBillingData(null)
          // Keep isChartLoading as true when no data is available
        }
      } catch (error) {
        console.error("Error fetching billing data:", error)
        setBillingData(null)
        // Keep isChartLoading as true when there's an error
      }
    }

    // Default report is the entire Billing Account. A project is an optional filter.
    if (selectedBillingId) {
      fetchBillingData()
    }
  }, [selectedMonth, selectedProjects, selectedBillingId, reportFrom, reportTo])

  useEffect(() => {
    const fetchHistoricalBillingData = async () => {
      if (!selectedBillingId || !selectedMonth) return
      try {
        const prev1 = getPreviousMonth(selectedMonth)
        const prev2 = getPreviousMonth(prev1)
        const prev3 = getPreviousMonth(prev2)
        const months = [prev1, prev2, prev3].filter(Boolean)
        const history = await Promise.all(months.map(async (month) => {
          const params = new URLSearchParams({
            billing_account_id: selectedBillingId,
            year_month: `${month}-01`,
            from: `${month}-01`,
            to: getMonthEnd(month),
          })
          if (selectedProjects.length) params.set("project_ids", selectedProjects.join(","))
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-by-service-by-project?${params}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          })
          if (!response.ok) throw new Error(`Historical billing API request failed with status ${response.status}`)
          return response.json()
        }))
        setHistoricalBillingData(history)
      } catch (error) {
        console.error("Error fetching historical billing data:", error)
        setHistoricalBillingData([])
      }
    }
    fetchHistoricalBillingData()
  }, [selectedMonth, selectedProjects, selectedBillingId])

  // Process the billing data whenever it changes
  useEffect(() => {
    if (billingData) {
      processBillingData()
    }
  }, [billingData, historicalBillingData, showCumulative, chartType, enabledSavings])

  // Process the billing data from the API
  const processBillingData = () => {
    try {
      console.log("Processing billing data...")

      if (!billingData || !billingData["billing by service and project data"]) {
        console.error("Billing data is not available or in unexpected format")
        setProcessedData(null)
        return
      }

      // Get all dates and sort them chronologically
      const dates = Object.keys(billingData["billing by service and project data"])
        .filter((date) => date.startsWith(selectedMonth))
        .sort()

      // If no dates available, don't process
      if (dates.length === 0) {
        console.log("No dates found in billing data")
        setProcessedData(null)
        return
      }

      // Get all unique service names
      const allServices = new Set()
      for (const date of dates) {
        const dayServices = billingData["billing by service and project data"][date]
        dayServices.forEach((service) => {
          if (service.service_name) {
            allServices.add(service.service_name)
          }
        })
      }
      const serviceNames = Array.from(allServices)

      // Process data for each date
      const dailyData = dates.map((date) => {
        const dayServices = billingData["billing by service and project data"][date]

        // Group and sum costs by service name using subtotal
        const serviceCosts = {}

        dayServices.forEach((service) => {
          const { service_name, subtotal } = service

          // Skip entries without a service name
          if (!service_name) return

          // Add to service costs using subtotal (which already includes cost - discounts)
          if (!serviceCosts[service_name]) {
            serviceCosts[service_name] = 0
          }
          const selectedSavings = savingsOptions.reduce(
            (sum, option) => sum + (enabledSavings[option.key] ? Number(service[option.key] || 0) : 0),
            0,
          )
          serviceCosts[service_name] += Number(subtotal || 0) + selectedSavings
        })

        // Calculate total using subtotals
        const total = Object.values(serviceCosts).reduce((sum, cost) => sum + Number(cost), 0)

        return {
          date,
          formattedDate: formatDate(date),
          services: serviceCosts,
          total,
        }
      })

      // Chart.js draws the first dataset at the bottom of a stacked bar.
      // Order services from the smallest total to the largest so the largest
      // usage is consistently displayed as the top layer.
      const totalsByService = {}
      serviceNames.forEach((service) => {
        totalsByService[service] = dailyData.reduce((sum, day) => sum + (day.services[service] || 0), 0)
      })
      const stackedServiceNames = [...serviceNames].sort(
        (first, second) => totalsByService[first] - totalsByService[second],
      )

      // Apply cumulative calculation if needed
      let processedBillingData = dailyData
      if (showCumulative) {
        processedBillingData = dailyData.map((day, index, array) => {
          if (index === 0) return day

          const cumulativeServices = {}

          // Initialize with current day's services
          Object.keys(day.services).forEach((service) => {
            cumulativeServices[service] = day.services[service]
          })

          // Add previous days' values
          for (let i = 0; i < index; i++) {
            Object.keys(array[i].services).forEach((service) => {
              if (!cumulativeServices[service]) {
                cumulativeServices[service] = 0
              }
              cumulativeServices[service] += array[i].services[service]
            })
          }

          const cumulativeTotal = Object.values(cumulativeServices).reduce((sum, val) => sum + Number(val), 0)

          return {
            ...day,
            services: cumulativeServices,
            total: cumulativeTotal,
          }
        })
      }

      const actualDays = dailyData.filter((day) => day.date.startsWith(selectedMonth) && Math.abs(day.total) > 0)
      const lastActualDate = actualDays[actualDays.length - 1]?.date
      processedBillingData = processedBillingData.filter((day) => !lastActualDate || day.date <= lastActualDate)
      const forecastDays = billingInsights.forecastDays.map((day) => ({
        ...day,
        formattedDate: formatDate(day.date),
        services: {},
        forecast: true,
      }))
      const futureForecastDays = forecastDays.filter((day) => day.date > lastActualDate)
      const chartDays = [...processedBillingData, ...futureForecastDays]
      const forecastByDate = Object.fromEntries(forecastDays.map((day) => [day.date, day.total]))

      // Prepare chart data
      const chartData = {
        labels: chartDays.map((day) => day.formattedDate),
        datasets: [
          ...stackedServiceNames.map((service, index) => ({
          label: service,
          data: chartDays.map((day) => day.services[service] || 0),
          backgroundColor: getServiceColor(service, index),
          borderColor: getServiceColor(service, index),
          borderWidth: chartType === "line" ? 2 : 1,
          fill: chartType === "line" ? false : true,
          tension: chartType === "line" ? 0.4 : 0,
          stack: chartType === "bar" ? "stack1" : undefined,
          })),
          ...(billingInsights.isOngoingMonth && chartType === "bar" ? [{
          label: "Forecasted cost",
          data: chartDays.map((day) => forecastByDate[day.date] || 0),
          backgroundColor: "rgba(148, 163, 184, 0.35)",
          borderColor: "rgba(148, 163, 184, 0.65)",
          borderWidth: 1,
          stack: "stack1",
          }] : []),
        ],
      }

      // Prepare pie chart data using subtotals
      // Calculate total cost using subtotals for billing summary
      const calculatedTotalCost = Object.values(totalsByService).reduce((sum, cost) => sum + Number(cost), 0)

      // Compute previous month totals by service from historicalBillingData[0]
      const prevTotalsByService = {}
      const prevMonthData = historicalBillingData[0]?.["billing by service and project data"] || {}
      Object.values(prevMonthData).forEach((dayServices) => {
        if (!Array.isArray(dayServices)) return
        dayServices.forEach((service) => {
          const { service_name, subtotal } = service
          if (!service_name) return
          if (!prevTotalsByService[service_name]) {
            prevTotalsByService[service_name] = 0
          }
          const selectedSavings = savingsOptions.reduce(
            (sum, option) => sum + (enabledSavings[option.key] ? Number(service[option.key] || 0) : 0),
            0,
          )
          prevTotalsByService[service_name] += Number(subtotal || 0) + selectedSavings
        })
      })

      const hasPrevData = Object.keys(prevTotalsByService).length > 0
      const prevTotalCost = hasPrevData
        ? Object.values(prevTotalsByService).reduce((sum, cost) => sum + Number(cost), 0)
        : null

      const overallChangePercent =
        prevTotalCost !== null && prevTotalCost > 0
          ? ((calculatedTotalCost - prevTotalCost) / prevTotalCost) * 100
          : billingInsights.changePercent

      // Sort services by cost for summary
      const sortedServices = Object.entries(totalsByService).sort((a, b) => b[1] - a[1])

      // Get top services for summary
      const topService = sortedServices.length > 0 ? sortedServices[0][0] : null
      const topServiceCost = sortedServices.length > 0 ? sortedServices[0][1] : 0
      const secondService = sortedServices.length > 1 ? sortedServices[1][0] : null
      const secondServiceCost = sortedServices.length > 1 ? sortedServices[1][1] : 0

      const topServicePrevCost = topService && hasPrevData ? (prevTotalsByService[topService] || 0) : null
      const topServiceChangePercent =
        topServicePrevCost !== null && topServicePrevCost > 0
          ? ((topServiceCost - topServicePrevCost) / topServicePrevCost) * 100
          : topServicePrevCost === 0 && topServiceCost > 0
          ? 100
          : null

      const secondServicePrevCost = secondService && hasPrevData ? (prevTotalsByService[secondService] || 0) : null
      const secondServiceChangePercent =
        secondServicePrevCost !== null && secondServicePrevCost > 0
          ? ((secondServiceCost - secondServicePrevCost) / secondServicePrevCost) * 100
          : secondServicePrevCost === 0 && secondServiceCost > 0
          ? 100
          : null

      // Update billing summary - use calculated total for current month, invoice data for historical
      setBillingSummary((prev) => ({
        ...prev,
        totalCost: calculatedTotalCost,
        prevTotalCost,
        overallChangePercent,
        topService,
        topServiceCost,
        topServiceChangePercent,
        secondService,
        secondServiceCost,
        secondServiceChangePercent,
        serviceCount: Object.keys(totalsByService).filter(service => totalsByService[service] > 0).length,
      }))

      const pieChartData = {
        labels: serviceNames,
        datasets: [
          {
            data: serviceNames.map((service) => totalsByService[service]),
            backgroundColor: serviceNames.map((service, index) => getServiceColor(service, index)),
            borderColor: serviceNames.map((service, index) => getServiceColor(service, index)),
            borderWidth: 1,
          },
        ],
      }

      // Prepare top resources billing using subtotals & MoM change percentage
      const topResources = sortedServices.slice(0, 5).map(([name, cost]) => {
        const prevCost = hasPrevData ? (prevTotalsByService[name] || 0) : null
        let changePercent = null
        if (prevCost !== null) {
          if (prevCost > 0) {
            changePercent = ((cost - prevCost) / prevCost) * 100
          } else if (cost > 0) {
            changePercent = 100
          } else {
            changePercent = 0
          }
        }
        return {
          name,
          cost,
          prevCost,
          amount: formatCurrency(cost),
          changePercent,
        }
      })

      setProcessedData({
        chartData,
        pieChartData,
        topResources,
        totalCost: calculatedTotalCost,
      })

      console.log("Processed data:", {
        dates: dates.length,
        services: serviceNames.length,
        dailyData: dailyData.length,
        chartData: chartData.labels.length,
        totalCost: calculatedTotalCost,
      })
    } catch (error) {
      console.error("Error processing billing data:", error)
    }
  }

  // Chart options
  const isMobileScreen = typeof window !== "undefined" && window.innerWidth < 768;
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !isMobileScreen,
        position: "top",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: "circle",
          font: { size: 11 },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        filter: (context) => {
          const rankedDatasets = context.chart.data.datasets
            .map((dataset, datasetIndex) => ({
              datasetIndex,
              value: Number(dataset.data[context.dataIndex] || 0),
            }))
            .filter((item) => item.value > 0)
            .sort((first, second) => second.value - first.value)

          return rankedDatasets.slice(0, 5).some((item) => item.datasetIndex === context.datasetIndex)
        },
        itemSort: (first, second) => Number(second.raw || 0) - Number(first.raw || 0),
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || ""
            const value = context.raw
            return `${label}: ${formatCurrency(value)}`
          },
          footer: (tooltipItems) => {
            if (!tooltipItems.length) return ""
            const dataIndex = tooltipItems[0].dataIndex
            const hiddenCount = tooltipItems[0].chart.data.datasets.filter(
              (dataset) => Number(dataset.data[dataIndex] || 0) > 0,
            ).length - tooltipItems.length
            return hiddenCount > 0 ? `${hiddenCount} below` : ""
          },
        },
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#111",
        bodyColor: "#333",
        footerColor: "#111",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        cornerRadius: 8,
      },
      title: {
        display: false,
        text: showCumulative ? "Cumulative Billing by Service" : "Daily Billing by Service",
        font: { size: 16, weight: "bold" },
        padding: { top: 10, bottom: 20 },
        color: "#333",
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: typeof window !== "undefined" && window.innerWidth < 640 ? 10 : 11 },
          autoSkip: true,
          maxTicksLimit: typeof window !== "undefined" && window.innerWidth < 640 ? 6 : (isCompactChart ? 10 : 15),
        },
        stacked: chartType === "bar",
      },
      y: {
        stacked: chartType === "bar",
        ticks: {
          callback: (value) => `Rp${(value / 1000).toFixed(0)}K`,
          font: { size: typeof window !== "undefined" && window.innerWidth < 640 ? 10 : 11 },
        },
        grid: { color: "rgba(0, 0, 0, 0.05)" },
      },
    },
    elements: {
      point: {
        radius: chartType === "line" ? 3 : 0,
        hoverRadius: chartType === "line" ? 5 : 0,
      },
      line: { tension: 0.4 },
      bar: { borderRadius: 4 },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  }

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobileScreen ? "bottom" : "right",
        display: true,
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: "circle",
          padding: isMobileScreen ? 8 : 10,
          font: { size: isMobileScreen ? 11 : 10 },
          // Limit the number of labels shown
          filter: (legendItem, data) => {
            // Show only top 8 items in legend
            return data.datasets[0].data.indexOf(data.datasets[0].data[legendItem.index]) < 8
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || ""
            const value = context.raw
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            return `${label}: ${formatCurrency(value)} (${percentage}%)`
          },
        },
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#111",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
      },
      title: {
        display: true,
        font: { size: 16, weight: "bold" },
        padding: { top: 10, bottom: 20 },
        color: "#333",
      },
    },
    cutout: "50%",
  }

  // Fetch project list (ubah endpoint & simpan billing_id)
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/billing/accounts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`)
        }

        const accounts = await response.json()
        setBillingAccounts(accounts)

        if (accounts.length > 0) {
          const defaultAccount = accounts.find((account) => account.billing_account_id === requestedBillingId) || accounts[0]
          setSelectedProjects(requestedProjectIds)
          setSelectedBillingId(defaultAccount.billing_account_id)
          await fetchProjectsForBillingAccount(defaultAccount.billing_account_id)
        }
      } catch (error) {
        console.error("Error fetching projects:", error)
      }
    }

    fetchProjects()
  }, [searchParams, requestedBillingId]) // Also react to URL filters

  // Fetch invoice summary data - only for historical months
  useEffect(() => {
    const fetchInvoiceSummary = async () => {
      if (!selectedBillingId) return

      // Only fetch invoice data for historical months
      if (isCurrentMonth(selectedMonth)) {
        console.log("Current month detected, skipping invoice API call")
        // Update monthName even if skipping invoice API call
        setBillingSummary((prev) => ({
          ...prev,
          monthName: format(new Date(selectedMonth), "MMMM yyyy"),
        }))
        return
      }

      try {
        // Ambil billingAccountId dari selectedBillingId, bukan localStorage
        const billingAccountId = selectedBillingId || "01E318-E504D7-6A5BBF"

        // Convert YYYY-MM to YYYY-MM-01 format for the API
        const invoiceMonth = `${selectedMonth}-01`

        const params = new URLSearchParams({
          billing_account_id: billingAccountId,
          invoice_month: invoiceMonth,
          from: reportFrom,
          to: reportTo,
        })
        if (selectedProjects.length) params.set("project_ids", selectedProjects.join(","))
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/billing/summary-billing-invoices?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })

        if (!response.ok) {
          throw new Error(`Invoice API request failed with status ${response.status}`)
        }

        const data = await response.json()
        setInvoiceData(data)
        console.log("Fetched invoice data:", data)

        // Update billing summary with invoice data for historical months
        if (data && data["monthly invoices data"] && data["monthly invoices data"].length > 0) {
          setBillingSummary((prev) => ({
            ...prev,
            monthName: format(new Date(selectedMonth), "MMMM yyyy"),
          }))
        } else {
          // Tetap update monthName jika tidak ada data invoice
          setBillingSummary((prev) => ({
            ...prev,
            monthName: format(new Date(selectedMonth), "MMMM yyyy"),
          }))
        }
      } catch (error) {
        console.error("Error fetching invoice summary:", error)
        // Tetap update monthName jika error
        setBillingSummary((prev) => ({
          ...prev,
          monthName: format(new Date(selectedMonth), "MMMM yyyy"),
        }))
      }
    }

    fetchInvoiceSummary()
  }, [selectedMonth, selectedProjects, selectedBillingId, reportFrom, reportTo])

  // Only render the loading screen on initial load
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

        <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
          {/* Sidebar */}
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

          <div
            className={`flex-1 flex items-center justify-center transition-all duration-300 pt-20 sm:pt-24 ${
              sidebarOpen ? "lg:ml-64" : "ml-0"
            }`}
          >
            <LoadingScreen />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        <div
          className={`flex-1 min-w-0 max-w-full overflow-x-hidden px-3.5 sm:px-6 py-3 sm:py-6 transition-all duration-300 pt-16 sm:pt-20 relative ${
            sidebarOpen ? "lg:ml-64" : "ml-0"
          }`}
        >
          <div className="flex flex-col">
            <div className="bg-white rounded-lg shadow-sm mb-3 sm:mb-5 px-3.5 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-3">
              <div>
                <h1 className="text-base sm:text-xl font-bold text-gray-900">Billing Reports</h1>
                <p className="text-xs sm:text-sm text-gray-500">Monitor billing and cost distribution</p>
              </div>
              {isAdmin ? (
                <div className="w-full sm:w-80">
                  <label htmlFor="billing-account-select" className="block text-[11px] sm:text-xs font-medium text-gray-500 mb-1">Customer</label>
                  <select
                    id="billing-account-select"
                    value={selectedBillingId || ""}
                    onChange={(e) => {
                      const billingAccountId = e.target.value
                      setSelectedBillingId(billingAccountId)
                      setSelectedProjects([])
                      setAvailableProjects([])
                      fetchProjectsForBillingAccount(billingAccountId).catch((error) => console.error("Error fetching projects:", error))
                      updateReportUrl([], billingAccountId, selectedMonth)
                    }}
                    className="w-full appearance-none bg-white border border-gray-200 rounded-md px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 min-h-[36px] sm:min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {billingAccounts.map((account) => (
                      <option key={account.billing_account_id} value={account.billing_account_id}>{account.company_name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-left sm:text-right">
                  <p className="text-xs sm:text-sm font-semibold text-gray-800">{selectedAccount?.company_name || currentUser.company_name}</p>
                  <p className="text-[11px] sm:text-xs text-gray-500">Billing Account: {selectedBillingId || currentUser.billing_account_id}</p>
                </div>
              )}
            </div>
            {/* Summary Billing Container - Updated with real data */}
            <div className="bg-white rounded-lg shadow-sm mb-3 sm:mb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                {/* Left section - Billing Summary */}
                <div className="p-3.5 sm:p-5">
                  {isChartLoading ? (
                    <BillingSummarySkeleton />
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 mb-2">
                        <div className="flex items-center gap-1.5 sm:gap-2 relative group">
                          <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Billing Summary for {billingSummary.monthName}</h3>
                          <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 cursor-pointer" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M12 7v6M12 17v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                          <span className="absolute left-8 top-1 z-10 opacity-0 group-hover:opacity-80 group-hover:translate-y-0 -translate-y-2 transition-all duration-200 bg-gray-800 text-white text-xs rounded px-3 py-2 w-72 shadow-lg cursor-pointer pointer-events-none group-hover:pointer-events-auto">
                            This billing summary is based on the data processed from the GCP API. Please note that this
                            billing summary might not always be the same as showed on GCP due to the delay in data
                            processing. If you have any questions, please contact our support team.
                          </span>
                        </div>
                        {isCurrentMonth(selectedMonth) && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-md shrink-0"
                            title="Ongoing billing is calculated through D-1 and may vary slightly due to GCP data synchronization"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                            Through D-1 • Minor sync variance may occur
                          </span>
                        )}
                      </div>

                      <div className="mt-2.5 sm:mt-4">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(billingSummary.totalCost)}</p>
                          {!isCurrentMonth(selectedMonth) && billingSummary.overallChangePercent !== null && billingSummary.overallChangePercent !== undefined && (
                            <span
                              className={`inline-flex items-center gap-0.5 text-[11px] sm:text-xs font-semibold px-2 py-0.5 rounded-full ${
                                billingSummary.overallChangePercent > 0
                                  ? "text-red-700 bg-red-50"
                                  : billingSummary.overallChangePercent < 0
                                  ? "text-emerald-700 bg-emerald-50"
                                  : "text-gray-600 bg-gray-100"
                              }`}
                              title={
                                billingSummary.prevTotalCost !== null
                                  ? `${billingSummary.overallChangePercent > 0 ? "+" : ""}${billingSummary.overallChangePercent.toFixed(1)}% vs previous month (${formatCurrency(billingSummary.prevTotalCost)})`
                                  : ""
                              }
                            >
                              {billingSummary.overallChangePercent > 0 ? "↑" : billingSummary.overallChangePercent < 0 ? "↓" : ""}{" "}
                              {Math.abs(Math.round(billingSummary.overallChangePercent))}% vs last month
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                          Total billing across {billingSummary.serviceCount} services
                        </p>
                        {!isCurrentMonth(selectedMonth) && billingSummary.prevTotalCost !== null && billingSummary.prevTotalCost > 0 && (
                          <p
                            className={`text-xs sm:text-sm font-semibold mt-1.5 flex items-center gap-1 ${
                              billingSummary.totalCost > billingSummary.prevTotalCost
                                ? "text-red-600"
                                : billingSummary.totalCost < billingSummary.prevTotalCost
                                ? "text-emerald-600"
                                : "text-gray-500"
                            }`}
                          >
                            <span>
                              {billingSummary.totalCost > billingSummary.prevTotalCost
                                ? `↑ +${formatCurrency(billingSummary.totalCost - billingSummary.prevTotalCost)} vs previous month`
                                : billingSummary.totalCost < billingSummary.prevTotalCost
                                ? `↓ -${formatCurrency(billingSummary.prevTotalCost - billingSummary.totalCost)} vs previous month`
                                : "No change vs previous month"}
                            </span>
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Right section - Top Services */}
                <div className="p-3.5 sm:p-5">
                  {isChartLoading ? (
                    <TopServicesSkeleton />
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Top Services</h3>
                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M12 7v6M12 17v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>

                      <div className="mt-2.5 sm:mt-4 space-y-2">
                        {billingSummary.topService ? (
                          <>
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <div className="flex items-center min-w-0 pr-2">
                                <div
                                  className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                                  style={{ backgroundColor: getServiceColor(billingSummary.topService, 0) }}
                                ></div>
                                <p className="font-medium text-gray-700 truncate">{billingSummary.topService}:</p>
                                <p className="ml-1.5 font-bold text-gray-900 shrink-0">{formatCurrency(billingSummary.topServiceCost)}</p>
                              </div>
                              {!isCurrentMonth(selectedMonth) && billingSummary.topServiceChangePercent !== null && billingSummary.topServiceChangePercent !== undefined && (
                                <span
                                  className={`text-[10px] sm:text-xs font-semibold shrink-0 flex items-center gap-0.5 ${
                                    billingSummary.topServiceChangePercent > 0
                                      ? "text-red-500"
                                      : billingSummary.topServiceChangePercent < 0
                                      ? "text-emerald-600"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {billingSummary.topServiceChangePercent > 0 ? "↑" : billingSummary.topServiceChangePercent < 0 ? "↓" : ""}{" "}
                                  {Math.abs(Math.round(billingSummary.topServiceChangePercent))}%
                                </span>
                              )}
                            </div>

                            {billingSummary.secondService && (
                              <div className="flex items-center justify-between text-xs sm:text-sm">
                                <div className="flex items-center min-w-0 pr-2">
                                  <div
                                    className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                                    style={{ backgroundColor: getServiceColor(billingSummary.secondService, 1) }}
                                  ></div>
                                  <p className="font-medium text-gray-700 truncate">{billingSummary.secondService}:</p>
                                  <p className="ml-1.5 font-bold text-gray-900 shrink-0">{formatCurrency(billingSummary.secondServiceCost)}</p>
                                </div>
                                {!isCurrentMonth(selectedMonth) && billingSummary.secondServiceChangePercent !== null && billingSummary.secondServiceChangePercent !== undefined && (
                                  <span
                                    className={`text-[10px] sm:text-xs font-semibold shrink-0 flex items-center gap-0.5 ${
                                      billingSummary.secondServiceChangePercent > 0
                                        ? "text-red-500"
                                        : billingSummary.secondServiceChangePercent < 0
                                        ? "text-emerald-600"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    {billingSummary.secondServiceChangePercent > 0 ? "↑" : billingSummary.secondServiceChangePercent < 0 ? "↓" : ""}{" "}
                                    {Math.abs(Math.round(billingSummary.secondServiceChangePercent))}%
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-gray-500">No billing data available</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white rounded-lg shadow-sm p-2.5 sm:p-4 mb-3 sm:mb-5 border border-gray-100">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2.5 sm:gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                  {/* Project filter */}
                  <div className="relative w-full lg:w-72">
                    <button
                      type="button"
                      onClick={openProjectMenu}
                      className="w-full min-h-[36px] sm:min-h-[42px] flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                    >
                      <span className="truncate">{selectedProjects.length ? `${selectedProjects.length} project${selectedProjects.length > 1 ? "s" : ""} selected` : "All projects"}</span>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" /></svg>
                    </button>
                    {projectMenuOpen && (
                      <div
                        className="absolute z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                        style={{ width: "min(600px, calc(100vw - 2rem))" }}
                      >
                        <div className="px-5 pt-5 pb-3">
                          <h3 className="text-xl font-medium text-gray-900">Projects</h3>
                          <div className="mt-4 flex items-center border-b border-gray-300 pb-2">
                            <svg className="w-5 h-5 mr-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M6 12h12m-9 6h6" /></svg>
                            <span className="font-medium mr-4">Filter</span>
                            <input autoFocus value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} placeholder="Type to filter" className="w-full outline-none text-gray-700 placeholder:text-gray-400" />
                          </div>
                        </div>
                        <label className="flex items-center gap-4 px-5 py-4 border-y border-gray-200 cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" checked={projectOptions.length > 0 && pendingProjects.length === projectOptions.length} onChange={() => setPendingProjects(pendingProjects.length === projectOptions.length ? [] : projectOptions.map((project) => project.value))} className="w-5 h-5 accent-blue-600" />
                          <span className="text-base">Select {pendingProjects.length} of {projectOptions.length}</span>
                        </label>
                        <div className="max-h-[26rem] overflow-y-auto">
                          {filteredProjectOptions.map((project) => (
                            <label key={project.value} className="flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50">
                              <input type="checkbox" checked={pendingProjects.includes(project.value)} onChange={() => togglePendingProject(project.value)} className="mt-1 w-5 h-5 accent-blue-600" />
                              <span><span className="block text-base text-gray-900">{project.label}</span><span className="block text-sm text-gray-500 mt-1">{project.detail || project.value}</span></span>
                            </label>
                          ))}
                          {!filteredProjectOptions.length && <p className="px-5 py-8 text-center text-gray-500">No projects found</p>}
                        </div>
                        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
                          <button type="button" onClick={() => setPendingProjects([])} disabled={!pendingProjects.length} className="text-blue-600 disabled:text-gray-300 font-medium">Deselect all</button>
                          <div className="flex items-center gap-7"><button type="button" onClick={() => setProjectMenuOpen(false)} className="text-blue-600 font-medium">Cancel</button><button type="button" onClick={applyProjectFilter} className="text-blue-600 font-medium">Apply</button></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Date picker */}
                  <div className="relative w-full lg:w-56 [&>.react-datepicker-wrapper]:w-full [&>.react-datepicker-wrapper_input]:w-full">
                    <ReactDatePicker
                      selected={new Date(selectedMonth)}
                      onChange={(date) => {
                        const month = format(date, "yyyy-MM")
                        setSelectedMonth(month)
                        if (selectedBillingId) updateReportUrl(selectedProjects, selectedBillingId, month)
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      className="w-full appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[36px] sm:min-h-[42px]"
                      popperClassName="react-datepicker-popper"
                      calendarClassName="react-datepicker-calendar"
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400">
                      <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                  {/* Savings selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setSavingsMenuOpen((open) => !open)}
                      className={`flex items-center gap-1.5 sm:gap-2 border rounded-md px-2.5 sm:px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium shadow-2xs min-h-[36px] sm:min-h-[42px] ${
                        Object.values(enabledSavings).some(Boolean)
                          ? "bg-blue-50 border-blue-200 text-blue-700"
                          : "bg-white border-gray-200 text-gray-700"
                      }`}
                    >
                      Savings
                      {Object.values(enabledSavings).filter(Boolean).length > 0 && (
                        <span className="bg-blue-600 text-white text-[10px] sm:text-xs rounded-full px-1.5 py-0.5">
                          {Object.values(enabledSavings).filter(Boolean).length}
                        </span>
                      )}
                      <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {savingsMenuOpen && (
                      <div className="absolute left-0 top-11 sm:top-12 z-30 w-80 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">Savings</h3>
                            <p className="text-[11px] text-gray-500 mt-0.5">Apply credits to billing totals</p>
                          </div>
                          <button
                            type="button"
                            className="text-xs font-medium text-blue-600"
                            onClick={() => {
                              const allEnabled = savingsOptions.every((option) => enabledSavings[option.key])
                              setEnabledSavings(
                                Object.fromEntries(savingsOptions.map((option) => [option.key, !allEnabled])),
                              )
                            }}
                          >
                            {savingsOptions.every((option) => enabledSavings[option.key])
                              ? "Disable all"
                              : "Enable all"}
                          </button>
                        </div>

                        <div className="space-y-3">
                          {savingsOptions.map((option) => {
                            const enabled = Boolean(enabledSavings[option.key])
                            return (
                              <label key={option.key} className="flex items-center justify-between gap-3 cursor-pointer">
                                <span className="min-w-0">
                                  <span className="block text-xs sm:text-sm text-gray-700">{option.label}</span>
                                  <span className="block text-[11px] sm:text-xs text-emerald-600">
                                    {formatCurrency(Math.abs(savingsTotals[option.key]))}
                                  </span>
                                </span>
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={enabled}
                                  onChange={() =>
                                    setEnabledSavings((current) => ({
                                      ...current,
                                      [option.key]: !current[option.key],
                                    }))
                                  }
                                />
                                <span
                                  className={`relative block h-5 w-10 flex-none rounded-full transition-colors ${
                                    enabled ? "bg-blue-600" : "bg-gray-300"
                                  }`}
                                >
                                  <span
                                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                      enabled ? "translate-x-5" : "translate-x-1"
                                    }`}
                                  />
                                </span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cumulative toggle */}
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <button
                      type="button"
                      aria-pressed={showCumulative}
                      onClick={() => setShowCumulative((v) => !v)}
                      className="relative w-8 sm:w-10 h-4.5 sm:h-5 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full transition"
                      tabIndex={0}
                    >
                      <span
                        className={`block w-8 sm:w-10 h-4.5 sm:h-5 rounded-full transition-colors duration-200 ${
                          showCumulative ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></span>
                      <span
                        className={`absolute left-0 top-0.5 w-3.5 sm:w-4 h-3.5 sm:h-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                          showCumulative ? "translate-x-4 sm:translate-x-5" : "translate-x-0.5 sm:translate-x-1"
                        }`}
                      ></span>
                    </button>
                    <label
                      htmlFor="show-cumulative"
                      className="text-xs sm:text-sm text-gray-700 select-none cursor-pointer"
                      onClick={() => setShowCumulative((v) => !v)}
                    >
                      Show cumulative
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Chart */}
            <div className="bg-white rounded-lg shadow-sm p-3.5 sm:p-6 mb-3 sm:mb-5">
              {isChartLoading ? (
                <MainChartSkeleton />
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-6 gap-2 sm:gap-3">
                    <div>
                      <h2 className="text-sm sm:text-base font-semibold mb-0.5 text-gray-900">
                        {showCumulative ? "Cumulative" : "Daily"} Billing by Service
                      </h2>
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        {selectedProjects.length === 1 && selectedProjects[0] === ACCOUNT_LEVEL_PROJECT
                          ? "Charges not specific to a project"
                          : selectedProjects.length
                          ? `${selectedProjects.length} selected project${selectedProjects.length > 1 ? "s" : ""}`
                          : `All projects • Billing Account ${selectedBillingId || ""}`}
                      </p>
                    </div>

                    {/* Chart Type Toggle in Header */}
                    <div className="flex items-center gap-1 self-start sm:self-auto bg-gray-100 p-0.5 sm:p-1 rounded-lg">
                      <button
                        type="button"
                        className={`p-1 sm:p-1.5 rounded-md text-xs font-medium transition-all ${
                          chartType === "bar" ? "bg-white text-blue-600 shadow-xs" : "text-gray-500 hover:text-gray-900"
                        }`}
                        onClick={() => setChartType("bar")}
                        title="Bar Chart"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M8 13V17M12 9V17M16 5V17"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`p-1 sm:p-1.5 rounded-md text-xs font-medium transition-all ${
                          chartType === "line" ? "bg-white text-blue-600 shadow-xs" : "text-gray-500 hover:text-gray-900"
                        }`}
                        onClick={() => setChartType("line")}
                        title="Line Chart"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M3 16.5L9 10.5L13 14.5L21 6.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {processedData && processedData.chartData ? (
                    <div className="h-[230px] sm:h-[380px]">
                      {chartType === "bar" ? (
                        <Bar data={processedData.chartData} options={chartOptions} />
                      ) : (
                        <Line data={processedData.chartData} options={chartOptions} />
                      )}
                    </div>
                  ) : (
                    <div className="h-[230px] sm:h-[380px] flex items-center justify-center">
                      <p className="text-xs sm:text-sm text-gray-500">No billing data available for the selected period</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Three Additional Containers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-5">
              {/* Top Resources Billing */}
              <div className="bg-white rounded-lg shadow-sm p-3.5 sm:p-6">
                {isChartLoading ? (
                  <TopResourcesSkeleton />
                ) : (
                  <>
                    <h2 className="text-sm sm:text-base font-semibold mb-3 text-gray-900">Top Resources Billing</h2>
                    {processedData && processedData.topResources.length > 0 ? (
                      <ul className="space-y-2 sm:space-y-3 divide-y divide-gray-100">
                        {processedData.topResources.map((resource, index) => (
                          <li key={index} className="flex justify-between items-center py-2 sm:py-2.5">
                            <span className="text-xs sm:text-sm font-medium text-gray-900 truncate pr-2">{resource.name}</span>
                            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                              {!isCurrentMonth(selectedMonth) && (
                                <span
                                  className={`w-12 sm:w-14 text-right text-xs sm:text-sm font-semibold flex items-center justify-end gap-0.5 ${
                                    resource.changePercent > 0
                                      ? "text-red-500"
                                      : resource.changePercent < 0
                                      ? "text-emerald-600"
                                      : "text-gray-400"
                                  }`}
                                  title={
                                    resource.prevCost !== undefined && resource.prevCost !== null
                                      ? `${resource.changePercent > 0 ? "+" : ""}${resource.changePercent.toFixed(1)}% vs previous month (${formatCurrency(resource.prevCost)})`
                                      : ""
                                  }
                                >
                                  {resource.changePercent !== null && resource.changePercent !== undefined ? (
                                    <>
                                      <span>{resource.changePercent > 0 ? "↑" : resource.changePercent < 0 ? "↓" : ""}</span>
                                      <span>{Math.abs(Math.round(resource.changePercent))}%</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </span>
                              )}
                              <span className="text-[11px] sm:text-xs font-semibold bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-right shrink-0">
                                {resource.amount}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-500">No resources data available</p>
                    )}
                  </>
                )}
              </div>

              {/* Cost Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-3.5 sm:p-6 overflow-hidden">
                {isChartLoading ? (
                  <PieChartSkeleton />
                ) : (
                  <>
                    <h2 className="text-sm sm:text-base font-semibold mb-3 text-gray-900">Cost Distribution</h2>
                    <div className="h-[240px] sm:h-[300px]">
                      {processedData && processedData.pieChartData.datasets[0].data.some((value) => value > 0) ? (
                        <Pie data={processedData.pieChartData} options={pieOptions} />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <p className="text-xs sm:text-sm text-gray-500">No distribution data available</p>
                        </div>
                      )}
                    </div>
                    {processedData && processedData.pieChartData.labels.length > 8 && (
                      <p className="text-[11px] sm:text-xs text-gray-500 mt-2 text-center">
                        Showing top 8 of {processedData.pieChartData.labels.length} services
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Usage Reports */}
              <div>
                {isChartLoading ? <UsageReportsSkeleton /> : <UsageReports
                    selectedProjects={selectedProjects.filter((p) => p !== ACCOUNT_LEVEL_PROJECT)}
                    selectedProject={selectedProjects.length === 1 && selectedProjects[0] !== ACCOUNT_LEVEL_PROJECT ? selectedProjects[0] : null}
                    selectedBillingId={selectedBillingId}
                    customerName={selectedAccount?.company_name || currentUser.company_name || ""}
                    availableProjects={availableProjects}
                  />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
